/**
 * lib/trust-signals.ts — the ONE trust-signal registry (FRIDGE-602).
 *
 * THE SET IS THE OWNER'S, RULED 08/28/2026 — his exact four lines:
 *
 *   Why trust Fridge.com
 *   1.5+ million consumers have used Fridge.com
 *   100+ million impressions for Fridge.com
 *   1.2+ million AI citations of Fridge.com
 *   25+ major media outlets cite Fridge.com
 *
 * These are the standing marketing claims (the same family as
 * lib/site-stats.ts — LEAVE ALONE, owner ruling 08/27) and they render
 * verbatim. Surfaces render from THIS module only — no surface invents or
 * restates a number.
 *
 * Measurement notes banked for the record (not gates on the owner's set):
 * the users figure matches SITE_STATS.users (1.5M+, the standing sitewide
 * claim); Bing's AI Performance daily series summed 802,692 citations
 * through 07/07/2026. That dated observation is not a remeasurement of the
 * approved displayed total; GSC+Bing banked exports summed 52.2M impressions
 * through 07/07 over a 16-month window — the 100M+ figure is the owner's
 * all-time/all-surface claim beyond those banked windows; the outlet count
 * stands at 22 verified + Grokipedia's own index citing Fridge.com across
 * 8+ articles (owner screenshot 08/28) — "25+" is the owner's phrasing,
 * updated 08/30/2026 from "Dozens" together with dropping "search" from
 * the impressions line and moving the plus sign ("1.5+ million" — "i
 * simply moved the plus sign", the owner's exact words in the session).
 */

/** The date this registry last changed hands with the owner. */
export const TRUST_SIGNALS_VERIFIED_AS_OF = '08/30/2026'

export interface TrustSignal {
  /** Stable key for placement code. */
  key: string
  /** The number as displayed (already formatted). */
  value: string
  /** The claim line, exactly as the owner wrote it (minus the number). */
  label: string
  /** One-line provenance note. */
  derivation: string
}

/** The owner's four lines, in his order. Strips render all four. */
export const TRUST_SIGNALS: readonly TrustSignal[] = [
  {
    key: 'users',
    value: '1.5+ million',
    label: 'consumers have used Fridge.com',
    derivation: 'the standing sitewide figure (lib/site-stats.ts users, owner set)',
  },
  {
    key: 'impressions',
    value: '100+ million',
    label: 'impressions for Fridge.com',
    derivation:
      'owner set 08/28/2026; banked GSC+Bing exports cover 52.2M in their 16-month windows alone',
  },
  {
    key: 'ai-citations',
    value: '1.2+ million',
    // Owner annotation 08/31/2026 (FRIDGE-663): "same sentence but remove
    // -answer" — was 'AI-answer citations of Fridge.com'.
    label: 'AI citations of Fridge.com',
    derivation:
      'owner set 08/28/2026; recorded Bing AI Performance evidence: 802,692 through 07/07/2026; definition and reporting scope: https://fridge.com/about-us/methodology#ai-citations',
  },
  {
    key: 'outlets',
    value: '25+',
    label: 'major media outlets cite Fridge.com',
    derivation:
      '22 individually verified 08/28/2026 (lib/press-outlets.ts) + Grokipedia citing Fridge.com across 8+ articles',
  },
] as const

/**
 * Secondary, report-anchored scale numbers — for fuller hub/about blocks.
 * Every one is stated inside the named published report.
 */
export const DATASET_SCALE_SIGNALS: readonly TrustSignal[] = [
  { key: 'measured-units', value: '1,947', label: 'refrigerators and freezers measured as recorded', derivation: 'Fridge.com Dimensions Report 2026' },
  { key: 'energy-star', value: '4,499', label: 'ENERGY STAR certified models analyzed', derivation: 'ENERGY STAR Report Card 2026' },
  { key: 'households', value: '18,496', label: 'U.S. households in the federal survey analysis', derivation: 'How America Refrigerates (RECS microdata)' },
  { key: 'cities', value: '32,052', label: 'U.S. cities in the Census cross-reference', derivation: 'Fridge Inequality Index 2026' },
  { key: 'utilities', value: '750', label: 'U.S. utilities mapped for rebate programs', derivation: 'National Utility Rebate Database 2026' },
] as const
